import {
    ParticipantListEventOutputsRequest,
    ParticipantListEventOutputsResponse,
    ParticipantListTeamOutputsRequest,
    ParticipantListTeamOutputsResponse,
} from '@amzn/event-engine-events-sdk/clients/eventengineeventsservice';
import { useLazyRequest, usePaginator } from '@amzn/event-engine-js-utils';
import { AWSError } from 'aws-sdk';
import React, { useCallback, useEffect, useState } from 'react';
import { EEEvents } from 'services';

import { DEFAULT_PAGINATION_LIMIT } from './config';
import EventOutputsTable from './EventOutputsTable';
import {
    EventOutputsTableContainerProps,
    ParticipantStackOutputTableItem,
} from './types';

const EventOutputsTableContainer = ({
    participantId,
}: EventOutputsTableContainerProps) => {
    const [allEventOutputsData, setAllEventOutputsData] = useState<
        ParticipantStackOutputTableItem[]
    >([]);

    const [allTeamOutputsData, setAllTeamOutputsData] = useState<
        ParticipantStackOutputTableItem[]
    >([]);
    const [
        getEventOutputs,
        {
            data: eventOutputsData,
            isError: isEventOutputsError,
            isLoading: isEventOutputsLoading,
        },
    ] = useLazyRequest<
        typeof EEEvents.getParticipantListEventOutputs,
        AWSError
    >(EEEvents.getParticipantListEventOutputs, {});

    const [
        getTeamOutputs,
        {
            data: eventTeamOutputsData,
            isError: isEventTeamOutputsError,
            isLoading: isEventTeamOutputsLoading,
        },
    ] = useLazyRequest<typeof EEEvents.getParticipantListTeamOutputs, AWSError>(
        EEEvents.getParticipantListTeamOutputs,
        {}
    );

    const paginatorEvent = usePaginator<
        ParticipantListEventOutputsRequest,
        ParticipantListEventOutputsResponse
    >(getEventOutputs, DEFAULT_PAGINATION_LIMIT, 'outputs');

    const paginatorTeams = usePaginator<
        ParticipantListTeamOutputsRequest,
        ParticipantListTeamOutputsResponse
    >(getTeamOutputs, DEFAULT_PAGINATION_LIMIT, 'outputs');

    const getPaginatedDataEvent = useCallback((fresh: boolean = false) => {
        if (!participantId) {
            return;
        }
        return paginatorEvent.getPaginatedData<ParticipantListEventOutputsRequest>(
            0,
            '*',
            {
                fresh,
                clientArgs: { participantId },
            }
        );
    }, []);

    const getPaginatedDataTeams = useCallback((fresh: boolean = false) => {
        if (!participantId) {
            return;
        }
        return paginatorTeams.getPaginatedData<ParticipantListTeamOutputsRequest>(
            0,
            '*',
            {
                fresh,
                clientArgs: { participantId },
            }
        );
    }, []);

    useEffect(() => {
        getPaginatedDataEvent();
        getPaginatedDataTeams();
    }, []);

    useEffect(() => {
        if (eventTeamOutputsData?.outputs) {
            setAllTeamOutputsData([
                ...allTeamOutputsData,
                ...eventTeamOutputsData.outputs,
            ]);
        }
    }, [eventTeamOutputsData]);

    useEffect(() => {
        if (eventOutputsData?.outputs) {
            setAllEventOutputsData([
                ...allEventOutputsData,
                ...eventOutputsData.outputs,
            ]);
        }
    }, [eventOutputsData]);

    return (
        <EventOutputsTable
            outputs={[...allEventOutputsData, ...allTeamOutputsData]}
            error={isEventOutputsError || isEventTeamOutputsError}
            loading={isEventOutputsLoading || isEventTeamOutputsLoading}
        />
    );
};

export default EventOutputsTableContainer;
